import type { Underlying } from 'models/underlying';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type {
  IUnderlyingDetailsOption,
  IUnderlyingFilterOptions,
  IUnderlyingUniverse,
} from 'shared/services/backends/underlyingBackend.service';
import type { IHttpClient } from 'utils/http.old/httpClient.model';

export interface UnderlyingSearchResult {
  underlyings: Underlying[];
}
export interface IUnderlyingsApi {
  filter(option: IUnderlyingFilterOptions): Observable<UnderlyingSearchResult>;
  fetch(option: IUnderlyingFilterOptions): Observable<IUnderlyingUniverse>;
  download(option: IUnderlyingFilterOptions): void;
  details(option: IUnderlyingDetailsOption): Observable<Record<string, Underlying>>;
  map(option: IUnderlyingDetailsOption): Observable<ReadonlyArray<Nullable<Underlying>>>;
}

export function underlyingsApi(http: IHttpClient): IUnderlyingsApi {
  const baseUrl = 'api/v1/underlyings';
  return {
    filter(option: IUnderlyingFilterOptions): Observable<UnderlyingSearchResult> {
      return http
        .post<UnderlyingSearchResult>({
          url: 'api/v2/underlyings/filter',
          body: option,
        })
        .pipe(map(({ payload }) => payload));
    },
    fetch(option: IUnderlyingFilterOptions): Observable<IUnderlyingUniverse> {
      return http
        .post<IUnderlyingUniverse>({
          url: baseUrl,
          body: option,
        })
        .pipe(map(({ payload }) => payload));
    },
    download(option: IUnderlyingFilterOptions): Observable<Underlying> {
      return http
        .post<Underlying>({
          url: baseUrl + '/csv',
          body: option,
        })
        .pipe(map(({ payload }) => payload));
    },
    details(option: IUnderlyingDetailsOption): Observable<Record<string, Underlying>> {
      return http
        .post<Record<string, Underlying>>({
          url: 'api/v2/underlyings/details',
          body: option,
        })
        .pipe(map(({ payload }) => payload));
    },
    map(option: IUnderlyingDetailsOption): Observable<ReadonlyArray<Underlying | null>> {
      return http
        .post<Record<string, Underlying>>({
          url: 'api/v2/underlyings/details',
          body: option,
        })
        .pipe(
          map(({ payload: underlyingMap }) => {
            return buildMappingResult(option.underlyingsIds, underlyingMap);
          }),
        );
    },
  };
}

export function buildMappingResult(
  idsToMap: string[] = [],
  underlyingMap: Record<string, Underlying>,
): ReadonlyArray<Underlying | null> {
  return idsToMap.map(id => {
    const matchingId = Object.keys(underlyingMap).find(underlyingId => {
      const underlying = underlyingMap[underlyingId];
      if (id.toLocaleLowerCase() === underlyingId.toLocaleLowerCase()) {
        return true;
      }
      if (underlying?.compositeId && id.toLowerCase() === underlying.compositeId.toLowerCase()) {
        return true;
      }
      return false;
    });
    return matchingId ? underlyingMap[matchingId] : null;
  });
}
